import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowDownToLine, ArrowUp, ArrowUpToLine, ChevronRight, Sliders, Trash2, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { colorForClassIndex } from '../../utils/classColors';
import type { BoundingBox, Class as ClassModel } from '../../types/api';

/**
 * BoxContextMenu — right-click menu on a bounding box.
 *
 * Per PIVOTS_2026_05_31.md § 12:
 *   Delete             [D]
 *   ─────────────────────
 *   Change class    ▸  apple
 *                      banana
 *                      orange
 *   ─────────────────────
 *   Edit dimensions…
 *   ─────────────────────
 *   Cancel            [Esc]
 *
 * Anchored at the click position (x, y in viewport coords). Hides on click-
 * outside, ESC, or explicit Cancel. Renders as a fixed-position floating
 * element on top of everything. Auto-flips if it would render off-screen.
 */
interface BoxContextMenuProps {
    isDarkMode?: boolean;
    box: BoundingBox;
    classes: ClassModel[];
    /** Viewport position of the right-click. */
    x: number;
    y: number;
    onClose: () => void;
    onDelete: () => void;
    onChangeClass: (classIndex: number) => void;
    onOpenInProperties: () => void;
    onBringForward: () => void;
    onSendBackward: () => void;
    onBringToFront: () => void;
    onSendToBack: () => void;
}

const MENU_WIDTH = 240;
const SUBMENU_WIDTH = 200;

export default function BoxContextMenu({
    isDarkMode = false,
    box,
    classes,
    x,
    y,
    onClose,
    onDelete,
    onChangeClass,
    onOpenInProperties,
    onBringForward,
    onSendBackward,
    onBringToFront,
    onSendToBack,
}: BoxContextMenuProps) {
    const { t } = useTranslation('canvas');
    const { t: tCommon } = useTranslation('common');
    const rootRef = useRef<HTMLDivElement>(null);
    const [submenuOpen, setSubmenuOpen] = useState(false);

    // Click-outside + ESC dismiss.
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        // mousedown lets us catch the click that opens a different menu before
        // it actually renders. Use capture so we run before component handlers.
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [onClose]);

    // Flip menu if it would overflow viewport.
    const flipX = x + MENU_WIDTH > window.innerWidth;
    const flipY = y + 220 > window.innerHeight;
    const left = flipX ? x - MENU_WIDTH : x;
    const top = flipY ? Math.max(8, y - 220) : y;

    // Submenu position relative to its parent row — flip if needed.
    const submenuFlipX = left + MENU_WIDTH + SUBMENU_WIDTH > window.innerWidth;

    const panelCls = cn(
        'fixed z-[100] rounded-lg shadow-2xl border py-1 text-[13px]',
        isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800',
    );
    const itemBaseCls = cn('w-full flex items-center gap-2 px-3 py-1.5 transition-colors cursor-pointer text-left');
    const itemHover = isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50';
    const dividerCls = cn('my-1 border-t', isDarkMode ? 'border-gray-700' : 'border-gray-200');
    const kbdCls = cn('ml-auto text-[10px] uppercase tracking-wider', isDarkMode ? 'text-gray-500' : 'text-gray-400');

    return (
        <div ref={rootRef} style={{ left, top, width: MENU_WIDTH }} className={panelCls} role="menu">
            {/* Delete */}
            <button type="button" onClick={() => { onDelete(); onClose(); }} className={cn(itemBaseCls, itemHover)}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                {t('delete_box', { defaultValue: 'Delete box' })}
                <span className={kbdCls}>D</span>
            </button>

            <div className={dividerCls} />

            {/* Change class — submenu */}
            <div
                className="relative"
                onMouseEnter={() => setSubmenuOpen(true)}
                onMouseLeave={() => setSubmenuOpen(false)}
            >
                <div className={cn(itemBaseCls, itemHover)}>
                    <Sliders className="h-3.5 w-3.5 opacity-70" />
                    {t('change_class', { defaultValue: 'Change class' })}
                    <ChevronRight className={cn('h-3.5 w-3.5 ml-auto', isDarkMode ? 'text-gray-500' : 'text-gray-400')} />
                </div>

                {submenuOpen && classes.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: submenuFlipX ? -SUBMENU_WIDTH : MENU_WIDTH,
                            width: SUBMENU_WIDTH,
                        }}
                        className={panelCls.replace('fixed', 'absolute')}
                    >
                        {classes.map((c) => {
                            const current = c.class_index === box.class_index;
                            const color = c.color || colorForClassIndex(c.class_index);
                            return (
                                <button
                                    key={c.class_index}
                                    type="button"
                                    onClick={() => {
                                        if (!current) onChangeClass(c.class_index);
                                        onClose();
                                    }}
                                    disabled={current}
                                    className={cn(
                                        itemBaseCls,
                                        current
                                            ? isDarkMode ? 'bg-gray-800 opacity-60 cursor-default' : 'bg-gray-100 opacity-60 cursor-default'
                                            : itemHover,
                                    )}
                                >
                                    <span
                                        className="h-3 w-3 rounded-full shrink-0"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className="truncate">{c.name}</span>
                                    {current && <span className={kbdCls}>✓</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className={dividerCls} />

            {/* Z-order — single-step + jump-to-extreme. Plain-English wording
                per user feedback ('better names may be needed'): 'Move up one'
                vs 'Move to top' is clearer than Forward/Backward + Front/Back
                jargon for users without design-tool background. */}
            <button
                type="button"
                onClick={() => { onBringForward(); onClose(); }}
                className={cn(itemBaseCls, itemHover)}
            >
                <ArrowUp className="h-3.5 w-3.5 opacity-70" />
                {t('move_up_one', { defaultValue: 'Move up one' })}
            </button>
            <button
                type="button"
                onClick={() => { onSendBackward(); onClose(); }}
                className={cn(itemBaseCls, itemHover)}
            >
                <ArrowDown className="h-3.5 w-3.5 opacity-70" />
                {t('move_down_one', { defaultValue: 'Move down one' })}
            </button>
            <button
                type="button"
                onClick={() => { onBringToFront(); onClose(); }}
                className={cn(itemBaseCls, itemHover)}
            >
                <ArrowUpToLine className="h-3.5 w-3.5 opacity-70" />
                {t('move_to_top', { defaultValue: 'Move to top' })}
            </button>
            <button
                type="button"
                onClick={() => { onSendToBack(); onClose(); }}
                className={cn(itemBaseCls, itemHover)}
            >
                <ArrowDownToLine className="h-3.5 w-3.5 opacity-70" />
                {t('move_to_bottom', { defaultValue: 'Move to bottom' })}
            </button>

            <div className={dividerCls} />

            {/* Open in Box Properties — explicit nav so right-click never
                surprise-jumps the user. */}
            <button
                type="button"
                onClick={() => { onOpenInProperties(); onClose(); }}
                className={cn(itemBaseCls, itemHover)}
            >
                <Sliders className="h-3.5 w-3.5 opacity-70" />
                {t('open_in_properties', { defaultValue: 'Open Properties' })}
            </button>

            <div className={dividerCls} />

            {/* Cancel */}
            <button type="button" onClick={onClose} className={cn(itemBaseCls, itemHover)}>
                <X className="h-3.5 w-3.5 opacity-70" />
                {tCommon('cancel')}
                <span className={kbdCls}>Esc</span>
            </button>
        </div>
    );
}
